import React, { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid2,
  Theme,
} from "@mui/material";
import { Add, Person } from "@mui/icons-material";
import { Controller, UseFormReturn } from "react-hook-form";
import {
  dialogAddPhoto,
  dialogBoxAdd,
  dialogBoxImage,
  dialogBoxImagePlaceholder,
  dialogBoxPreviewUrl,
  dialogMainBox,
  roleBox,
  userFormCont,
  userFormGridSize,
} from "../sxprops";
import { commonTextFieldProps, datePickerProps } from "../../textfield";
import { civilStatusOptions } from "../../../enums/civil-status";
import { genderOptions } from "../../../enums/gender";
import { bizboxUserDepartmentOptions } from "../../../enums/bizbox-user-department";
import { facilityUserDepartmentOptions } from "../../../enums/facility-user-department";
import { UserFormSchema } from "../api/schema";
import { RoleModel } from "../../../../main/roles/api/types";
import {
  useGetProductRoles,
  useGetRoles,
} from "../../../../main/roles/api/hooks";
import { useLocation, useParams } from "react-router-dom";
import useUserInvitationStore from "../api/store";
import { pascalToRegular, UserRoles } from "../../../enums/user-roles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { palettes } from "../../../themes/palletes";
import { ImageCropDialog } from "./image-crop-dialog";
import useMainStore from "../../../../main/landing-page/api/store";
import { getHighestRole } from "../../../utils/role-identifier";
import useFacilityStore from "../../../../main/facilities/api/store";
import useUsersStore from "../../../../main/users/api/store";

interface IUserForm {
  userForm: UseFormReturn<UserFormSchema, any, undefined>;
  walkthroughMode?: boolean;
  disabled: boolean;
  onChangeFacilityAdmin?: () => void;
  isLoading?: boolean;
  showDepartment?: boolean;
  variant?:
    | "client:create"
    | "client:update"
    | "facility:create"
    | "facility:update"
    | "user:create"
    | "user:update"
    | "userManage:update"
    | "userManage:create";
}

export const UserForm: React.FC<IUserForm> = ({
  userForm,
  disabled,
  walkthroughMode,
  variant = "client:create",
  isLoading = false,
  onChangeFacilityAdmin,
  showDepartment = true,
}) => {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = userForm;
  const { productCode } = useParams<{
    productCode: string;
    facilityCode: string;
  }>();
  const facilityStore = useFacilityStore();
  const userStore = useUsersStore();
  const isLaptopAndUp = useMediaQuery(theme.breakpoints.up("laptop"));
  const { data: productRoles } = useGetProductRoles(
    productCode ?? "",
    walkthroughMode,
  );
  const mainStore = useMainStore();

  type Modes = "client" | "bizbox";

  const { mode } = useParams<{ mode: Modes }>();

  const isInternal = mode === "bizbox";
  const { data: allRoles } = useGetRoles(isInternal, walkthroughMode);
  const selectedRole = watch("role") as {
    id: string;
    name: string;
    displayName: string;
    role: string;
  };

  const location = useLocation();

  const CSAccess =
    selectedRole?.displayName === "Bizbox User" &&
    pascalToRegular(getHighestRole(mainStore.currentUser?.roles)) ===
      "Bizbox Admin";

  const CSAdisabled =
    selectedRole?.displayName === "Bizbox Admin" &&
    pascalToRegular(getHighestRole(mainStore.currentUser?.roles)) ===
      "Bizbox Admin";

  const rolesToShow = React.useMemo(() => {
    if (!allRoles) return [];

    if (isInternal) {
      return allRoles.filter(
        (r) => r.name === "BizboxAdmin" || r.name === "BizboxUser",
      );
    }

    return allRoles;
  }, [allRoles, isInternal]);

  const isFacilityAdmin =
    mainStore.currentUser?.roles?.includes("FacilityAdmin");
  const isCoFacilityAdmin =
    mainStore.currentUser?.roles?.includes("CoFacilityAdmin");
  const isCoGroupAdmin = mainStore.currentUser?.roles?.includes("CoGroupAdmin");
  const isGroupAdmin = mainStore.currentUser?.roles?.includes("GroupAdmin");
  const isSuperAdmin = mainStore.currentUser?.roles?.includes("SuperAdmin");

  const viewOnly =
    facilityStore.isViewOnly && location.pathname === "/facilities";

  const isCurrentUser =
    !!mainStore.currentUser?.id &&
    mainStore.currentUser?.id === userStore.selectedUser?.id;

  const viewingOnly =
    (isFacilityAdmin || isCoFacilityAdmin || isCoGroupAdmin || isGroupAdmin) &&
    (viewOnly || (userStore.isEditModal && isCurrentUser));

  const {
    previewUrl,
    previewUrl2,
    originalFileName,
    openCropDialog,
    defaultRoleValue,
    setOpenCropDialog,
    setOriginalFileName,
    setPreviewUrl2,
    setPreviewUrl,
  } = useUserInvitationStore();

  const { isChangeAdminOpen } = useFacilityStore();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png"
    ) {
      const url = URL.createObjectURL(file);
      setPreviewUrl2(url);
      setOriginalFileName(file.name);
      setOpenCropDialog(true);
      event.target.value = "";
    } else {
      console.warn("Only JPG, JPEG, or PNG files are allowed.");
    }
  };

  useEffect(() => {
    setPreviewUrl(null);
    setPreviewUrl2(null);
    if (defaultRoleValue) {
      const selectedRole = productRoles?.find((option: RoleModel) => {
        const x: string = option.name;
        const y = UserRoles[defaultRoleValue];
        return x === y;
      });

      if (selectedRole) {
        setValue(
          "role",
          {
            id: selectedRole.id,
            name: selectedRole.name,
            displayName: selectedRole.displayName,
            role: selectedRole.role.toString(),
          },
          { shouldValidate: true },
        );
      }
    }
  }, [defaultRoleValue, productRoles, setPreviewUrl, setPreviewUrl2, setValue]);

  const firstRole = productRoles?.[0];

  useEffect(() => {
    if (!facilityStore.isEditModalOpen) {
      facilityStore.selectedFacility = undefined;
    }
  }, [facilityStore, facilityStore.isEditModalOpen]);

  useEffect(() => {
    if (
      (variant === "client:create" || variant === "client:update") &&
      !defaultRoleValue &&
      !userForm.getValues("role") &&
      productRoles?.length
    ) {
      const first = productRoles[0];

      setValue(
        "role",
        {
          id: first.id,
          name: first.name,
          displayName: first.displayName,
          role: String(first.role),
        },
        {
          shouldValidate: true,
          shouldDirty: false,
          shouldTouch: false,
        },
      );
    }
  }, [productRoles, defaultRoleValue, setValue, variant, userForm]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("laptop"));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={userFormCont(isLaptopAndUp)}>
        <Typography color="primary.main" fontWeight={400}>
          General Information
        </Typography>
        {variant === "facility:create" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "start",
              marginBottom: "10px",
              borderRadius: "8px",

              width: "100%",
              height: "50px",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={400}
              sx={{ mb: "10px" }}
            >
              Add or Update Facility Admin Details.
            </Typography>

            <Typography
              variant="caption"
              color="secondary.main"
              fontWeight={400}
              sx={{ mb: "10px" }}
            >
              Note: Facility Admin Information will be sent to the email
              provided in the Email Address.
            </Typography>
          </Box>
        )}
        {variant === "facility:update" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "start",
              marginBottom: "10px",
              borderRadius: "8px",

              width: "100%",
              height: "50px",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={400}
              sx={{ mb: "10px" }}
            >
              Add or Update Facility Admin Details.
            </Typography>
          </Box>
        )}
        {variant === "user:create" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "start",
              marginBottom: "10px",
              borderRadius: "8px",

              width: "100%",
              height: "50px",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={400}
              sx={{ mb: "10px" }}
            >
              Add or Update User Details
            </Typography>

            <Typography
              variant="caption"
              color="secondary.main"
              fontWeight={400}
              sx={{ mb: "10px" }}
            >
              Note: User Login information will be sent to the email provided in
              the Email Address.
            </Typography>
          </Box>
        )}
        {variant === "user:update" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "start",
              marginBottom: "10px",
              borderRadius: "8px",

              width: "100%",
              height: "50px",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={400}
              sx={{ mb: "10px" }}
            >
              Update User Details
            </Typography>
          </Box>
        )}
        <Box sx={dialogBoxImage}>
          <Box sx={dialogMainBox}>
            <Box sx={dialogBoxImagePlaceholder}>
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Profile"
                  sx={dialogBoxPreviewUrl}
                />
              ) : watch("photoUrl") ? (
                <Box
                  component="img"
                  src={watch("photoUrl")}
                  alt="Profile"
                  sx={dialogBoxPreviewUrl}
                />
              ) : (
                <Person style={{ width: 100, height: 100, fill: "white" }} />
              )}
            </Box>

            {variant !== "client:create" &&
              variant !== "client:update" &&
              !viewingOnly && (
                <Box
                  id={"add-photo-button"}
                  data-testid="add-photo-button"
                  onClick={handleAddClick}
                  sx={dialogBoxAdd}
                >
                  <Add sx={dialogAddPhoto} />
                </Box>
              )}
            <input
              ref={fileInputRef}
              type="file"
              data-testid="add-photo-file-input"
              style={{ display: "none" }}
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange}
              disabled={viewingOnly}
            />
          </Box>

          {(variant === "client:create" || variant === "client:update") &&
            !isSmallScreen && (
              <Box sx={roleBox}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id="role-box-dropdown"
                      fullWidth
                      select
                      disabled
                      sx={commonTextFieldProps}
                      value={field.value?.id ?? ""}
                      onChange={(e) => {
                        const selectedRole = productRoles?.find(
                          (option: RoleModel) => option.id === e.target.value,
                        );

                        field.onChange(
                          selectedRole
                            ? {
                                id: selectedRole.id,
                                name: selectedRole.name,
                                displayName: selectedRole.displayName,
                                role: String(selectedRole.role),
                              }
                            : null,
                        );
                      }}
                      error={!!errors.role}
                      helperText={errors.role?.message}
                      SelectProps={{
                        SelectDisplayProps: { "data-testid": "role-box-dropdown" } as any,
                        MenuProps: {
                          MenuListProps: {
                            sx: (theme: Theme) => ({
                              border: `1px solid ${theme.palette.text.secondary}`,
                              borderRadius: "5px",
                            }),
                          },
                        },
                      }}
                    >
                      {productRoles?.map((option: RoleModel) => (
                        <MenuItem key={option.id} value={option.id}>
                          <Typography color="primary.main">
                            {option.displayName}
                          </Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Box>
            )}

          {(variant === "userManage:create" ||
            variant === "userManage:update") &&
            !isSmallScreen && (
              <Box sx={roleBox}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => {
                    const currentId = field.value?.id ?? "";

                    return (
                      <TextField
                        select
                        fullWidth
                        sx={commonTextFieldProps}
                        value={currentId}
                        disabled={
                          CSAccess ||
                          CSAdisabled ||
                          viewingOnly ||
                          isLoading
                        }
                        onChange={(e) => {
                          const picked = rolesToShow?.find(
                            (opt) => opt.id === e.target.value,
                          );
                          field.onChange(
                            picked
                              ? {
                                  id: picked.id,
                                  name: picked.name,
                                  displayName: picked.displayName,
                                  role: picked.role,
                                }
                              : null,
                          );
                        }}
                        error={!!errors.role}
                        helperText={errors.role?.message}
                        SelectProps={{
                          SelectDisplayProps: { "data-testid": "role-box-dropdown" } as any,
                          MenuProps: {
                            MenuListProps: {
                              sx: (theme: Theme) => ({
                                border: `1px solid ${theme.palette.text.secondary}`,
                                borderRadius: "5px",
                              }),
                            },
                          },
                        }}
                      >
                        {rolesToShow?.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            <Typography color="primary.main">
                              {opt.displayName}
                            </Typography>
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  }}
                />
              </Box>
            )}

          {(variant === "userManage:create" ||
            variant === "userManage:update") &&
            !isSmallScreen && (
              <Box sx={roleBox}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => {
                    const currentId = field.value?.id ?? "";

                    return (
                      <TextField
                        select
                        fullWidth
                        sx={commonTextFieldProps}
                        value={currentId}
                        disabled={
                          CSAccess ||
                          CSAdisabled ||
                          viewingOnly ||
                          isLoading
                        }
                        onChange={(e) => {
                          const picked = rolesToShow?.find(
                            (opt) => opt.id === e.target.value,
                          );
                          field.onChange(
                            picked
                              ? {
                                  id: picked.id,
                                  name: picked.name,
                                  displayName: picked.displayName,
                                  role: picked.role,
                                }
                              : null,
                          );
                        }}
                        error={!!errors.role}
                        helperText={errors.role?.message}
                        SelectProps={{
                          SelectDisplayProps: { "data-testid": "role-box-dropdown" } as any,
                          MenuProps: {
                            MenuListProps: {
                              sx: (theme: Theme) => ({
                                border: `1px solid ${theme.palette.text.secondary}`,
                                borderRadius: "5px",
                              }),
                            },
                          },
                        }}
                      >
                        {rolesToShow?.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            <Typography color="primary.main">
                              {opt.displayName}
                            </Typography>
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  }}
                />
              </Box>
            )}

          {variant === "facility:update" && !isChangeAdminOpen && (
            <Box sx={roleBox}>
              <Box
                id="change-facility-admin-button"
                data-testid="change-facility-admin-button"
                onClick={
                  disabled || viewingOnly ? undefined : onChangeFacilityAdmin
                }
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "8px",
                  border: "1px solid",
                  borderColor:
                    disabled || viewingOnly ? "text.disabled" : "primary.main",
                  color:
                    disabled || viewingOnly ? "text.disabled" : "primary.main",
                  borderRadius: "10px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: `${palettes.$textLightSubtle}`,
                  },
                }}
              >
                Change Facility Admin
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: "20px" }}>
          {(variant === "client:create" || variant === "client:update") &&
            isSmallScreen && (
              <Box sx={roleBox} marginBottom={1}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      fullWidth
                      select
                      sx={commonTextFieldProps}
                      value={firstRole?.id || ""}
                      onChange={(e) => {
                        const selectedRole = productRoles?.find(
                          (option: RoleModel) => option.id === e.target.value,
                        );
                        if (selectedRole) {
                          field.onChange({
                            id: selectedRole.id,
                            name: selectedRole.name,
                            displayName: selectedRole.displayName,
                            role: selectedRole.role,
                          });
                        } else {
                          field.onChange(null);
                        }
                      }}
                      error={!!errors.role}
                      helperText={errors.role?.message}
                      SelectProps={{
                        SelectDisplayProps: { "data-testid": "role-box-dropdown" } as any,
                        MenuProps: {
                          MenuListProps: {
                            sx: (theme: Theme) => ({
                              border: `1px solid ${theme.palette.text.secondary}`,
                              borderRadius: "5px",
                            }),
                          },
                        },
                      }}
                    >
                      {productRoles?.map((option: RoleModel) => (
                        <MenuItem key={option.id} value={option.id}>
                          <Typography color="primary.main">
                            {option.displayName}
                          </Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Box>
            )}

          {(variant === "userManage:create" ||
            variant === "userManage:update") &&
            isSmallScreen && (
              <Box sx={roleBox} marginBottom={1.5}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => {
                    const currentId = field.value?.id ?? "";

                    return (
                      <TextField
                        select
                        fullWidth
                        sx={commonTextFieldProps}
                        value={currentId}
                        disabled={
                          CSAccess ||
                          CSAdisabled ||
                          viewingOnly ||
                          isLoading
                        }
                        onChange={(e) => {
                          const picked = rolesToShow?.find(
                            (opt) => opt.id === e.target.value,
                          );
                          field.onChange(
                            picked
                              ? {
                                  id: picked.id,
                                  name: picked.name,
                                  displayName: picked.displayName,
                                  role: picked.role,
                                }
                              : null,
                          );
                        }}
                        error={!!errors.role}
                        helperText={errors.role?.message}
                        SelectProps={{
                          SelectDisplayProps: { "data-testid": "role-box-dropdown" } as any,
                          MenuProps: {
                            MenuListProps: {
                              sx: (theme: Theme) => ({
                                border: `1px solid ${theme.palette.text.secondary}`,
                                borderRadius: "5px",
                              }),
                            },
                          },
                        }}
                      >
                        {rolesToShow?.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            <Typography color="primary.main">
                              {opt.displayName}
                            </Typography>
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  }}
                />
              </Box>
            )}

          <Grid2 container spacing={2}>
            <Grid2 container size={userFormGridSize}>
              <Controller
                name="prefix"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="prefix-profile-textfield"
                    {...field}
                    fullWidth
                    sx={commonTextFieldProps}
                    disabled={disabled || viewingOnly}
                    variant="outlined"
                    label="Prefix (Optional)"
                    autoComplete="off"
                    inputProps={{ "data-testid": "prefix-profile-textfield" }}
                    error={!!errors.prefix}
                    helperText={errors.prefix?.message}
                    InputLabelProps={{ shrink: Boolean(field.value) }}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="lastname-profile-textfield"
                    {...field}
                    fullWidth
                    sx={commonTextFieldProps}
                    disabled={disabled || viewingOnly}
                    variant="outlined"
                    label="Last Name"
                    autoComplete="off"
                    inputProps={{ "data-testid": "lastname-profile-textfield" }}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    InputLabelProps={{ shrink: Boolean(field.value) }}
                  />
                )}
              />
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="first-name-profile-textfield"
                    {...field}
                    fullWidth
                    sx={commonTextFieldProps}
                    disabled={disabled || viewingOnly}
                    variant="outlined"
                    label="First Name"
                    autoComplete="off"
                    inputProps={{ "data-testid": "first-name-profile-textfield" }}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    InputLabelProps={{ shrink: Boolean(field.value) }}
                  />
                )}
              />
              <Controller
                name="middleName"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="middlename-profile-textfield"
                    {...field}
                    fullWidth
                    sx={commonTextFieldProps}
                    disabled={disabled || viewingOnly}
                    variant="outlined"
                    label="Middle Name (Optional)"
                    autoComplete="off"
                    inputProps={{ "data-testid": "middlename-profile-textfield" }}
                    error={!!errors.middleName}
                    helperText={errors.middleName?.message}
                    InputLabelProps={{ shrink: Boolean(field.value) }}
                  />
                )}
              />
            </Grid2>

            <Grid2 container size={userFormGridSize}>
              <Controller
                name="customName"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="customname-profile-textfield"
                    {...field}
                    fullWidth
                    sx={commonTextFieldProps}
                    disabled={disabled || viewingOnly}
                    variant="outlined"
                    label="Custom Name (Optional)"
                    autoComplete="off"
                    inputProps={{ "data-testid": "customname-profile-textfield" }}
                    error={!!errors.customName}
                    helperText={errors.customName?.message}
                    InputLabelProps={{ shrink: Boolean(field.value) }}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="email-profile-textfield"
                    {...field}
                    fullWidth
                    sx={commonTextFieldProps}
                    disabled={disabled || viewingOnly}
                    variant="outlined"
                    label="Email Address"
                    autoComplete="off"
                    inputProps={{ "data-testid": "email-profile-textfield" }}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    InputLabelProps={{ shrink: Boolean(field.value) }}
                  />
                )}
              />
              <Controller
                name="civilStatus"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    id="civilstatus-profile-textfield"
                    {...field}
                    disabled={disabled || viewingOnly}
                    fullWidth
                    select
                    label="Civil Status"
                    sx={commonTextFieldProps}
                    value={field.value || 0}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    error={!!errors.civilStatus}
                    helperText={errors.civilStatus?.message}
                    slotProps={{
                      select: {
                        SelectDisplayProps: { "data-testid": "civilstatus-profile-textfield" } as any,
                        MenuProps: {
                          PaperProps: {
                            sx: (theme: Theme) => ({
                              border: `1px solid ${theme.palette.primary.main}`,
                              borderRadius: "8px",
                              boxShadow: 3,
                              maxHeight: 200,
                            }),
                          },
                        },
                      },
                    }}
                  >
                    {Object.values(civilStatusOptions).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Typography color="primary.main">
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="gender"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    id="gender-profile-textfield"
                    {...field}
                    disabled={disabled || viewingOnly}
                    fullWidth
                    select
                    label="Gender"
                    sx={commonTextFieldProps}
                    value={field.value || 0}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    error={!!errors.gender}
                    helperText={errors.gender?.message}
                    slotProps={{
                      select: {
                        SelectDisplayProps: { "data-testid": "gender-profile-textfield" } as any,
                        MenuProps: {
                          PaperProps: {
                            sx: (theme: Theme) => ({
                              border: `1px solid ${theme.palette.primary.main}`,
                              borderRadius: "8px",
                              boxShadow: 3,
                              maxHeight: 200,
                            }),
                          },
                        },
                      },
                    }}
                  >
                    {Object.values(genderOptions).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Typography color="primary.main">
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid2>

            {showDepartment &&
            (variant === "userManage:create" ||
              variant === "userManage:update" ||
              variant === "user:create" ||
              variant === "user:update") ? (
              <>
                <Grid2 container size={userFormGridSize}>
                  <Controller
                    name="suffix"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        id="suffix-profile-textfield"
                        {...field}
                        fullWidth
                        sx={commonTextFieldProps}
                        disabled={disabled || viewingOnly}
                        variant="outlined"
                        label="Suffix (Optional)"
                        autoComplete="off"
                        inputProps={{ "data-testid": "suffix-profile-textfield" }}
                        error={!!errors.suffix}
                        helperText={errors.suffix?.message}
                        InputLabelProps={{ shrink: Boolean(field.value) }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 container size={userFormGridSize}>
                  <Controller
                    name="department"
                    control={control}
                    render={({ field }) => {
                      const options =
                        variant === "userManage:create" ||
                        variant === "userManage:update"
                          ? bizboxUserDepartmentOptions
                          : facilityUserDepartmentOptions;
                      return (
                        <TextField
                          id="department-profile-textfield"
                          {...field}
                          disabled={disabled || viewingOnly}
                          fullWidth
                          select
                          label="Department"
                          sx={commonTextFieldProps}
                          value={field.value ?? 0}
                          onChange={(e) => {
                            field.onChange(Number(e.target.value));
                          }}
                          slotProps={{
                            select: {
                              SelectDisplayProps: { "data-testid": "department-profile-textfield" } as any,
                              MenuProps: {
                                PaperProps: {
                                  sx: (theme: Theme) => ({
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    borderRadius: "8px",
                                    boxShadow: 3,
                                    maxHeight: 200,
                                  }),
                                },
                              },
                            },
                          }}
                        >
                          {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Typography color="primary.main">
                                {option.label}
                              </Typography>
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    }}
                  />
                </Grid2>
              </>
            ) : (
              <Controller
                name="suffix"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="suffix-profile-textfield"
                    {...field}
                    fullWidth
                    sx={commonTextFieldProps}
                    disabled={disabled || viewingOnly}
                    variant="outlined"
                    label="Suffix (Optional)"
                    autoComplete="off"
                    error={!!errors.suffix}
                    helperText={errors.suffix?.message}
                    InputLabelProps={{ shrink: Boolean(field.value) }}
                  />
                )}
              />
            )}
          </Grid2>
        </Box>
        {openCropDialog && (
          <ImageCropDialog
            open={openCropDialog}
            onClose={() => setOpenCropDialog(false)}
            originalFileName={originalFileName}
            imageUrl={previewUrl2}
            onSave={(croppedFile) => {
              const newPreviewUrl = URL.createObjectURL(croppedFile);
              setPreviewUrl(newPreviewUrl);
              setOpenCropDialog(false);
              setValue("photoUrl", croppedFile);
            }}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};
